// modules/tenant-builder/steps/ModulesStep.tsx

import React from 'react';
import { Tenant } from '../../../core/types';
import { Calendar, Image, FileText, ShoppingCart, Star, Users, Fish } from 'lucide-react';

interface Props {
  data: Partial<Tenant>;
  onChange: (updates: Partial<Tenant>) => void;
}

const MODULES = [
  { key: 'bookings', name: 'Sistema de Reservas', icon: Calendar, description: 'Calendário e gestão de reservas online', recommended: true },
  { key: 'gallery', name: 'Galeria de Fotos', icon: Image, description: 'Galeria profissional de fotos e vídeos', recommended: true },
  { key: 'blog', name: 'Blog', icon: FileText, description: 'Publique notícias e histórias de pesca', recommended: false },
  { key: 'ecommerce', name: 'E-commerce', icon: ShoppingCart, description: 'Venda produtos e equipamentos', recommended: false },
  { key: 'reviews', name: 'Avaliações', icon: Star, description: 'Sistema de depoimentos de clientes', recommended: true },
  { key: 'team', name: 'Equipe/Guias', icon: Users, description: 'Apresente sua equipe e guias', recommended: true },
  { key: 'fishingLog', name: 'Registro de Pescarias', icon: Fish, description: 'Galeria de troféus e capturas', recommended: true },
];

export const ModulesStep: React.FC<Props> = ({ data, onChange }) => {
  const modules = data.enabledModules || {};

  const toggleModule = (key: string) => {
    onChange({
      enabledModules: {
        ...modules,
        [key]: !modules[key as keyof typeof modules]
      } as any
    });
  };

  return (
    <div className="space-y-6">
      <div  className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-800">
          💡 Selecione os módulos que você deseja ativar em seu site. Você pode ativar/desativar a qualquer momento.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {MODULES.map((module) => {
          const Icon = module.icon;
          const isEnabled = modules[module.key as keyof typeof modules];

          return (
            <div
              key={module.key}
              onClick={() => toggleModule(module.key)}
              className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                isEnabled
                  ? 'border-blue-600 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded-lg ${isEnabled ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  <Icon size={24} />
                </div>
                <div className="flex items-center gap-2">
                  {module.recommended && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                      Recomendado
                    </span>
                  )}
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isEnabled ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                  }`}>
                    {isEnabled && (
                      <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                </div>
              </div>
              <h3 className="font-semibold text-lg mb-1">{module.name}</h3>
              <p className="text-sm text-gray-600">{module.description}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-green-800">
          ✅ <strong>{Object.values(modules).filter(Boolean).length} módulos selecionados</strong> - Tudo funcionando perfeitamente!
        </p>
      </div>
    </div>
  );
};
